import { httpClient } from 'services/live4tv-client/http-client';
import { VIDEO_QUALITY_PRESETS } from '../constants';

const config = require('config/app.config');

/**
 * LiveKit Egress Service
 *
 * This service calls the l4-shop backend proxy endpoint for LiveKit egress operations.
 * The proxy handles authentication and forwards requests to the LiveKit server.
 */
class LiveKitEgressService {
    private proxyUrl = '/api/v1/livestudio/egress';

    /**
     * Start RTMP streaming via l4-shop backend proxy
     * @param token User authentication token for l4-shop API
     * @param roomName Name of the room to stream from
     * @param rtmpUrl RTMP URL to stream to
     * @param streamKey Stream key for the RTMP destination (optional)
     * @param quality Quality preset to use (HIGH, MEDIUM, LOW)
     * @returns Promise<any> Egress response with egress ID
     */
    startRtmpStream = async (
        token: string,
        roomName: string,
        rtmpUrl: string,
        streamKey?: string,
        quality: keyof typeof VIDEO_QUALITY_PRESETS = 'HIGH'
    ): Promise<any> => {
        try {
            const fullRtmpUrl = streamKey ? `${rtmpUrl}/${streamKey}` : rtmpUrl;
            const qualityPreset = VIDEO_QUALITY_PRESETS[quality];

            // Use room composite egress for streaming with correct format
            const body = {
                room_name: roomName,
                stream: {
                    protocol: 'RTMP',
                    urls: [fullRtmpUrl],
                },
                layout: 'speaker', // Default layout for room composite
                video: {
                    codec: 'H264_BASELINE',
                    width: qualityPreset.width,
                    height: qualityPreset.height,
                    framerate: qualityPreset.frameRate,
                    bitrate: qualityPreset.bitrate,
                },
                audio: {
                    codec: 'AAC',
                    bitrate: 128000,
                    frequency: 44100,
                },
            };
            console.log('startRtmpStream room composite body', body);

            const response = await httpClient.post(
                `${this.proxyUrl}/StartRoomCompositeEgress`,
                body,
                {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error: any) {
            throw new Error(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to start RTMP stream'
            );
        }
    }

    /**
     * Stop RTMP streaming via l4-shop backend proxy
     * @param token User authentication token for l4-shop API
     * @param egressId Egress ID to stop
     * @returns Promise<any> Stop response
     */
    stopRtmpStream = async (
        token: string,
        egressId: string
    ): Promise<any> => {
        try {
            const response = await httpClient.post(
                `${this.proxyUrl}/StopEgress`,
                {
                    egress_id: egressId,
                },
                {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to stop RTMP stream'
            );
        }
    }

    /**
     * Get egress status via l4-shop backend proxy
     * @param token User authentication token for l4-shop API
     * @param egressId Egress ID to check
     * @returns Promise<any> Status response
     */
    getEgressStatus = async (
        token: string,
        egressId: string
    ): Promise<any> => {
        try {
            const response = await httpClient.post(
                `${this.proxyUrl}/ListEgress`,
                {
                    room_name: '', // Empty to get all
                    egress_id: egressId,
                },
                {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to get egress status'
            );
        }
    }

    /**
     * List active egress streams for a room via l4-shop backend proxy
     * @param token User authentication token for l4-shop API
     * @param roomName Name of the room
     * @returns Promise<any> List of active egress streams
     */
    listActiveEgress = async (
        token: string,
        roomName: string
    ): Promise<any> => {
        try {
            const response = await httpClient.post(
                `${this.proxyUrl}/ListEgress`,
                {
                    room_name: roomName,
                },
                {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to list active egress streams'
            );
        }
    }
}

export const livekitEgressService = new LiveKitEgressService();
